import { getDeliveryTimeLabel } from '@/components/page-specific/catering/common/helpers/useDeliveryTimeLabel'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import {
  calculateOptions,
  getMinDateAllowed,
  timePopoverFormat,
} from '@caterdesk/utils--gm-validation'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { FormControl, FormHelperText, InputAdornment, MenuItem, Select } from '@mui/material'

import type React from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useVendorLocationData } from '../../store/vendorLocationStore'

type Props = {
  selectedDate?: number
  selectedTime?: string
  error?: string
  onTimeChange: (time: string) => void
}

const TimeSelect: React.FC<Props> = ({ selectedDate, selectedTime, error, onTimeChange }) => {
  const cutOff5pm = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)
  const tenant = useTenant()
  const { selectedVendorLocation, availableVendorLocations } = useVendorLocationData()

  const { t } = useTranslation()

  const { start: gmDeliveryTimeslotStart, end: gmDeliveryTimeslotEnd } = tenant.features
    ?.gmDeliveryTimeslots ?? { start: '', end: '' }

  const getTimeOptions = useCallback(() => {
    return calculateOptions(availableVendorLocations, selectedDate, true, {
      start: gmDeliveryTimeslotStart,
      end: gmDeliveryTimeslotEnd,
    })
  }, [availableVendorLocations, selectedDate, gmDeliveryTimeslotEnd, gmDeliveryTimeslotStart])

  const minDate = getMinDateAllowed(
    availableVendorLocations,
    {
      date: selectedDate,
      time: selectedTime,
    },
    cutOff5pm,
  )

  const leadTimeHours =
    selectedVendorLocation?.details?.effectiveMinimumNoticePeriod ??
    availableVendorLocations?.[0]?.details?.effectiveMinimumNoticePeriod ??
    null
  const leadTimeDate =
    typeof leadTimeHours === 'number' && Number.isFinite(leadTimeHours)
      ? new Date(Date.now() + leadTimeHours * 60 * 60 * 1000)
      : null
  const minDateWithLeadTime = leadTimeDate && leadTimeDate > minDate ? leadTimeDate : minDate

  const time = `${minDateWithLeadTime
    .getHours()
    .toString()}${minDateWithLeadTime.getMinutes().toString().padStart(2, '0')}`
  const chosenTime =
    minDateWithLeadTime && selectedDate && minDateWithLeadTime >= new Date(selectedDate)
      ? Number.parseInt(time, 10)
      : undefined

  const options = timePopoverFormat({
    ...getTimeOptions(),
    chosenTime,
  }).map((value) => ({ value, label: getDeliveryTimeLabel(tenant, value) }))

  const handleTimeChange = (option: { value: string } | null) => {
    if (!option) {
      return
    }

    onTimeChange(option.value)
  }

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Select
        size="small"
        disabled={!selectedDate}
        displayEmpty
        fullWidth
        onChange={(e) => handleTimeChange(e.target.value ? { value: e.target.value } : null)}
        startAdornment={
          <InputAdornment position="start">
            <AccessTimeIcon color="primary" />
          </InputAdornment>
        }
        renderValue={(selected) => {
          if (!selected) {
            return <span>{t('marketplace:time')}</span>
          }
          return getDeliveryTimeLabel(tenant, selected)
        }}
        value={selectedTime || ''}
        data-cy="time-select"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 4,
          },
        }}>
        <MenuItem value="" disabled>
          <em>{t('marketplace:time')}</em>
        </MenuItem>

        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  )
}

export default TimeSelect
